import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardBody, Col, Row, Table, Button, Spinner } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import {
  masterSystemServerList,
  masterSystemServerDeploy,
  masterSystemServerDeploySuccess,
} from "../../../../store/MasterSystemServer/action";
import { deployStatusMasterSystemServer } from "../../../../helpers/fakebackend_helper";
import SettingsPanel from "../../components/SettingsPanel";
import EnvVarsModal from "./EnvVarsModal";
import EnvVarTable from "./EnvVarTable";

// how often a running deployment is re-checked (Change #100655)
const DEPLOY_STATUS_POLL_MS = 10000;

const phaseBadgeClass = (status) => {
  if (!status?.phase) return "bg-secondary-subtle text-secondary";
  if (status.phase === "ACTIVE") return "bg-success-subtle text-success";
  if (status.phase === "SUPERSEDED") return "bg-secondary-subtle text-secondary";
  if (status.phase === "ERROR" || status.phase === "CANCELED")
    return "bg-danger-subtle text-danger";
  return "bg-warning-subtle text-warning";
};

const SystemServers = () => {
  const dispatch = useDispatch();

  // id of the server currently being deployed (so only its button spins)
  const [deployingId, setDeployingId] = useState(null);
  // latest DigitalOcean deployment per server id: summary, or { error }
  const [deployStatusById, setDeployStatusById] = useState({});
  const pollTimers = useRef({});
  // server whose DigitalOcean environment variables are open (Change #100749)
  const [envServer, setEnvServer] = useState(null);

  useEffect(() => {
    dispatch(masterSystemServerList());
  }, [dispatch]);

  // Fetch a server's latest deployment; keep polling until it finishes
  const fetchDeployStatus = useCallback(async (serverId) => {
    clearTimeout(pollTimers.current[serverId]);
    try {
      const res = await deployStatusMasterSystemServer({ id: serverId });
      const status =
        res?.status === 200
          ? res.deployment_status
          : { error: res?.message || "Status unavailable" };
      setDeployStatusById((prev) => ({ ...prev, [serverId]: status }));
      if (res?.status === 200 && status && !Number(status.flag_finished)) {
        pollTimers.current[serverId] = setTimeout(
          () => fetchDeployStatus(serverId),
          DEPLOY_STATUS_POLL_MS
        );
      }
    } catch (e) {
      setDeployStatusById((prev) => ({
        ...prev,
        [serverId]: { error: "Status unavailable" },
      }));
    }
  }, []);

  // stop polling when the screen is left
  useEffect(() => {
    const timers = pollTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const {
    serverListResponse,
    serverListLoading,
    deployResponse,
    deployLoading,
  } = useSelector((state) => ({
    serverListResponse: state.masterSystemServerReducer?.masterSystemServerList,
    serverListLoading:
      state.masterSystemServerReducer?.masterSystemServerListLoading,
    deployResponse: state.masterSystemServerReducer?.masterSystemServerDeploy,
    deployLoading:
      state.masterSystemServerReducer?.masterSystemServerDeployLoading,
  }));

  const serverList = Array.isArray(serverListResponse?.mastersystemserver_list)
    ? serverListResponse.mastersystemserver_list
    : [];

  // show each deployable server's last deployment once the list loads
  useEffect(() => {
    const list = Array.isArray(serverListResponse?.mastersystemserver_list)
      ? serverListResponse.mastersystemserver_list
      : [];
    list
      .filter((server) => !isEmpty(server.DigitalOceanAppId))
      .forEach((server) => fetchDeployStatus(server.Id));
  }, [serverListResponse, fetchDeployStatus]);

  // --- DEPLOY HANDLER ---
  const handleDeploy = (server) => {
    setDeployingId(server.Id);
    // single object payload with an id so apiURL builds /mastersystemserver/deploy/:id
    dispatch(masterSystemServerDeploy({ id: server.Id }));
  };

  // --- REDUX DEPLOY RESPONSE HANDLING ---
  useEffect(() => {
    if (isEmpty(deployResponse)) return;

    if (deployResponse?.status === 200) {
      toast.success(deployResponse?.message || "Deployment triggered successfully");
      // refresh the list (which also re-checks every server's deploy status,
      // so the new deployment is polled until it finishes)
      dispatch(masterSystemServerList());
    } else {
      toast.error(deployResponse?.message || "Deployment failed");
    }

    setDeployingId(null);
    // clear the response so the same action can be triggered again
    dispatch(masterSystemServerDeploySuccess({}));
  }, [deployResponse, dispatch]);

  return (
    <React.Fragment>
      <SettingsPanel
        title="System Servers"
        description="System server inventory, deployment controls and environment variables."
      >
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h4 className="card-title mb-0">System Servers</h4>
                  <Button
                    color="light"
                    size="sm"
                    className="border"
                    onClick={() => dispatch(masterSystemServerList())}
                    disabled={serverListLoading}
                  >
                    <i className="ri-refresh-line align-middle me-1"></i>
                    Refresh
                  </Button>
                </div>

                <div className="table-responsive">
                  <Table className="table table-striped table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Environment</th>
                        <th>Type</th>
                        <th>Stack</th>
                        <th>Description</th>
                        <th>URL</th>
                        <th>Manage</th>
                        <th className="text-end">Deploy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serverListLoading ? (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">
                            <Spinner size="sm" className="me-2" />
                            Loading servers...
                          </td>
                        </tr>
                      ) : serverList.length > 0 ? (
                        serverList.map((server, idx) => {
                          const canDeploy = !isEmpty(server.DigitalOceanAppId);
                          const isThisDeploying =
                            deployLoading && deployingId === server.Id;
                          return (
                            <tr key={server.Id || idx}>
                              <td className="fw-medium">{server.Name}</td>
                              <td>
                                <span className="badge bg-info-subtle text-info text-uppercase">
                                  {server.Environment}
                                </span>
                              </td>
                              <td>{server.ServerType}</td>
                              <td>{server.ServerStackStype}</td>
                              <td>{server.Description}</td>
                              <td>
                                {server.Url ? (
                                  <a
                                    href={server.Url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary text-decoration-none"
                                  >
                                    <i className="ri-external-link-line align-middle me-1"></i>
                                    Open
                                  </a>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {server.UrlManagement ? (
                                  <a
                                    href={server.UrlManagement}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary text-decoration-none"
                                  >
                                    <i className="ri-settings-3-line align-middle me-1"></i>
                                    Manage
                                  </a>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-end">
                                {canDeploy ? (
                                  <Button
                                    color="light"
                                    size="sm"
                                    className="border me-1"
                                    title="Environment variables"
                                    onClick={() => setEnvServer(server)}
                                  >
                                    <i className="ri-key-2-line align-middle me-1"></i>
                                    Env Vars
                                  </Button>
                                ) : null}
                                {canDeploy ? (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleDeploy(server)}
                                    disabled={deployLoading}
                                  >
                                    {isThisDeploying ? (
                                      <>
                                        <Spinner size="sm" className="me-1" />
                                        Deploying...
                                      </>
                                    ) : (
                                      <>
                                        <i className="ri-rocket-2-line align-middle me-1"></i>
                                        Deploy
                                      </>
                                    )}
                                  </Button>
                                ) : null}
                                {canDeploy && deployStatusById[server.Id] ? (
                                  <div className="small mt-1">
                                    {deployStatusById[server.Id].error ? (
                                      <span
                                        className="text-muted"
                                        title={deployStatusById[server.Id].error}
                                      >
                                        Status unavailable
                                      </span>
                                    ) : (
                                      <span
                                        className={`badge text-uppercase ${phaseBadgeClass(
                                          deployStatusById[server.Id]
                                        )}`}
                                        title={`Last deployment ${
                                          deployStatusById[server.Id].updated_at ||
                                          deployStatusById[server.Id].created_at ||
                                          ""
                                        }${
                                          deployStatusById[server.Id].cause
                                            ? " - " + deployStatusById[server.Id].cause
                                            : ""
                                        }`}
                                      >
                                        {!Number(
                                          deployStatusById[server.Id].flag_finished
                                        ) && (
                                          <Spinner
                                            size="sm"
                                            className="me-1"
                                            style={{ width: "0.6rem", height: "0.6rem" }}
                                          />
                                        )}
                                        {(deployStatusById[server.Id].phase || "").replace(
                                          /_/g,
                                          " "
                                        )}
                                        {deployStatusById[server.Id].steps_total
                                          ? ` ${deployStatusById[server.Id].steps_success || 0}/${deployStatusById[server.Id].steps_total}`
                                          : ""}
                                      </span>
                                    )}
                                  </div>
                                ) : null}
                                {!canDeploy ? (
                                  <span
                                    className="text-muted small"
                                    title="No DigitalOcean App linked"
                                  >
                                    N/A
                                  </span>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">
                            No system servers found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col lg="12">
            {/* shared environment variable table (Change #100749) */}
            <EnvVarTable onApplied={(ids) => ids.forEach((id) => fetchDeployStatus(id))} />
          </Col>
        </Row>
      </SettingsPanel>
      <EnvVarsModal
        open={!!envServer}
        onClose={() => setEnvServer(null)}
        server={envServer}
        servers={serverList.filter((s) => !isEmpty(s.DigitalOceanAppId))}
        // a spec change starts a deployment; poll each changed server's status
        onApplied={(ids) => ids.forEach((id) => fetchDeployStatus(id))}
      />
    </React.Fragment>
  );
};

export default SystemServers;
